/**
 * Reusable wallet balance card — drops onto the HomePage (top-right) and
 * anywhere a brand needs at-a-glance fund awareness.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Wallet, Plus } from 'lucide-react';
import { UrgencyBadge } from './ui/urgency-badge';

const API = process.env.REACT_APP_BACKEND_URL;
const LOW_BALANCE_THRESHOLD = 2500; // USD — below this we nudge a top-up

export default function WalletBalanceCard({ compact = false }) {
  const [state, setState] = useState({ balance: 0, loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/payments/wallet`, { withCredentials: true });
        if (!cancelled) setState({ ...r.data, loading: false });
      } catch {
        if (!cancelled) setState(s => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const low = !state.loading && (state.balance || 0) < LOW_BALANCE_THRESHOLD;

  if (compact) {
    return (
      <Link to="/billing" data-testid="wallet-balance-card-compact"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-[#00D4FF]/25 bg-[#00D4FF]/5 hover:bg-[#00D4FF]/10 transition-all">
        <Wallet className="h-3.5 w-3.5 text-[#00D4FF]" />
        <div>
          <p className="text-[8px] text-gray-500 uppercase tracking-widest font-bold">Wallet</p>
          <p className="text-[11px] font-black text-[#00D4FF]">${Math.round(state.balance || 0).toLocaleString()}</p>
        </div>
      </Link>
    );
  }

  return (
    <div className="relative rounded-2xl p-5 overflow-hidden" data-testid="wallet-balance-card"
         style={{
           background: low
             ? 'linear-gradient(135deg, rgba(255,77,148,0.08), rgba(10,10,10,0.9))'
             : 'linear-gradient(135deg, rgba(0,212,255,0.12), rgba(10,10,10,0.9))',
           border: `1px solid ${low ? 'rgba(255,77,148,0.3)' : 'rgba(0,212,255,0.3)'}`,
         }}>
      <div className="absolute -top-16 -right-14 w-48 h-48 rounded-full opacity-20 blur-3xl"
           style={{ background: low ? '#FF4D94' : '#00D4FF' }} />
      <div className="relative flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="h-3.5 w-3.5 text-[#00D4FF]" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-gray-400">Wallet Balance</span>
          </div>
          <p className="text-3xl lg:text-4xl font-black text-[#00D4FF] leading-none" data-testid="wallet-balance-value">
            ${Math.round(state.balance || 0).toLocaleString()}
          </p>
          <p className="text-[10px] text-gray-400 mt-1.5">Ready to deploy on your next Wave</p>
        </div>
        <Link to="/billing" data-testid="wallet-topup-btn"
              className="px-4 py-2.5 rounded-lg bg-[#FF4D94] text-white font-bold text-xs flex items-center gap-1.5 hover:brightness-110 hover:scale-[1.02] transition-all"
              style={{ boxShadow: '0 0 16px rgba(255,77,148,0.35)' }}>
          <Plus className="h-3.5 w-3.5" /> Top up
        </Link>
      </div>
      {low && (
        <div className="relative mt-3" data-testid="wallet-low-warning">
          <UrgencyBadge
            level="warning"
            label="Wallet short"
            context="top up before your next wave"
            testId="wallet-low-badge"
          />
        </div>
      )}
    </div>
  );
}
